import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import _ from "lodash";
import config from "../../config";
import docTypeFields from "../../models/DocTypeFields";

const fetchMenuItems = () => {
    return axios.get(`/method/restaurant_management.api.get_menu_items`);
};

const fetchAddons = () => {
    const params = {
        fields: JSON.stringify(docTypeFields.Addon),
        filters: JSON.stringify([["disabled", "=", 0]]),
        // filters: JSON.stringify([["item_type", "=", "Addons"], ["disabled", "=", 0]]),
        limit: 0, // Set the limit to 0 to load all items
    };

    return axios.get(`/resource/Item`, { params });
};

const fetchList = (doctype, filters, fields, sortBy, parent) => {
    const params = {
        fields: JSON.stringify(fields ? fields : ["*"]),
        limit_page_length: 0,
    };
    if (filters) {
        params["filters"] = JSON.stringify(filters);
    }

    if (parent) {
        params["parent"] = parent;
    }
    if (sortBy) {
        params["order_by"] = sortBy;
    }

    return axios.get(`resource/` + doctype, { params });
};

export const useItems = ({ allowedGroups }) => {
    const key = ["getMenuItems"];

    const filters = [
        ["item_type", "=", "Menu"],
        ["disabled", "=", 0],
    ];
    if (allowedGroups) {
        key.push(JSON.stringify(allowedGroups));

        if (allowedGroups.length == 1) {
            filters.push(["item_group", "descendants of", allowedGroups[0]]);
        } else {
            filters.push(["item_group", "in", allowedGroups]);
        }

        //
    }
    return useQuery(
        key,
        async () => {
            // const data = await fetchMenuItems();
            const data = await fetchList("Item", filters, ["*"], null, null);
            return data.data.data;
        },
        {
            select: transformMenuItems,
            cacheTime: 30 * 1000 * 60 * 60 * 24,
        }
    );
};

export const useAddons = (onSuccess, onError) => {
    return useQuery(
        ["getAddons"],
        async () => {
            let data = await fetchAddons();
            return data.data.data;
        },
        {
            onSuccess,
            onError,
        }
    );
};

const transformMenuItems = (data) => {
    const items = _.filter(data, { item_type: "Menu" });
    const convertedItems = items.map((item) => ({
        ...item,
        id: item.item_code,
        identifier: item.item_code,
        name: item.item_code,
        item_code: item.item_code,
        cover: `${config.baseUrl}${item.image}`,
        colors: ["#00AB55"],
        standard_rate: item.standard_rate,
        item_group: item.item_group,
        pos_qty: 0,
        rate: 0,
        price_list_rate: 0,
        discount_percentage: 0,
        item_tax_template: "",
        item_tax_rate: "{}",
        status: "Attending",
        has_serial_no: 0,
        serial_no: "",
        has_batch_no: 0,
        batch_no: "",
        notes: "",
        projected_qty: item.projected_qty,
        allowed_addons: item.allowed_addons
            ? JSON.parse(item.allowed_addons)
            : [],
    }));

    return convertedItems;
};

const fetchGroups = (filters) => {
    let params = {
        fields: JSON.stringify(docTypeFields.ItemGroup),
        limit: 0, // Set the limit to 0 to load all items
    };

    if (filters) {
        params.filters = JSON.stringify(filters);
    }
    return axios.get(`/resource/Item Group`, { params });
};

export const useItemGroups = (options = {}, onSuccess, onError) => {
    const { allowedGroups } = options || {};
    const normalizedAllowedGroups = Array.isArray(allowedGroups)
        ? allowedGroups.filter(Boolean)
        : null;

    return useQuery(
        ["getItemGroups", normalizedAllowedGroups || "ALL"],
        async () => {
            const filters = normalizedAllowedGroups
                ? [["parent_item_group", "in", normalizedAllowedGroups]]
                : [];
                filters.push(["showpos", "=", 1]);

            const data = await fetchGroups(filters);
            const groups = data.data.data || [];

            const convertedGroups = [...groups].map((_, index) => ({
                id: groups[index].item_group_name,
                name: groups[index].item_group_name,
                parent_item_group: groups[index].parent_item_group,
                is_group: groups[index].is_group,
                cover: config.baseUrl + groups[index].image,
                colors: ["#00AB55"],
            }));

            const groupedItems = _.groupBy(
                convertedGroups,
                "parent_item_group"
            );

            return groupedItems;
        },
        {
            onSuccess,
            onError,
            enabled: true,
        }
    );
};

const fetchSubGroups = (parentItem) => {
    let params = {
        fields: JSON.stringify(docTypeFields.ItemGroup),
        filters: JSON.stringify({ parent_item_group: parentItem }),
        limit: 0, // Set the limit to 0 to load all items
    };
    return axios.get(`/resource/Item Group`, { params });
};

export const useSubGroups = (parentItem, onSuccess, onError) => {
    return useQuery(
        ["getGroupSubGroups", parentItem],
        async () => {
            const data = await fetchSubGroups(parentItem);
            const groups = _.filter(
                data.data.data || [],
                (g) => g.showpos === 1
            );

            const convertedGroups = [...groups].map((_, index) => ({
                id: groups[index].item_group_name,
                name: groups[index].item_group_name,
                is_group: groups[index].is_group,
                cover: config.baseUrl + groups[index].image,
                colors: ["#00AB55"],
            }));

            return convertedGroups;
        },
        {
            // select: transformGroups,
            onSuccess,
            onError,
            enabled: !!parentItem,
        }
    );
};
